import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { Header } from "../components/Frumy";
import { ShieldCheck, UploadSimple, FileCsv, FilePdf, Sparkle, Bank, ArrowLeft, CircleNotch, Plus, X } from "@phosphor-icons/react";

export default function Upload() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const isPro = !!user?.is_pro;
  const inputRef = useRef();
  const pendingRef = useRef();
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, current}
  const [pending, setPending] = useState([]); // staged File[]
  const [accountName, setAccountName] = useState("");
  const [existingNames, setExistingNames] = useState([]);

  useEffect(() => {
    if (!isPro) return;
    axios.get(`${API}/scans`).then(({ data }) => {
      setExistingNames((data || []).map((s) => (s.account_name || "").trim().toLowerCase()));
    }).catch(() => {});
  }, [isPro]);

  useEffect(() => {
    if (pending.length > 0 && pendingRef.current) {
      pendingRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [pending.length]);

  const checkOverwrite = () => {
    if (isPro) {
      const name = accountName.trim();
      if (name && existingNames.includes(name.toLowerCase())) {
        return window.confirm(t("accountExists", { name }));
      }
    }
    return true;
  };

  const errorMessage = (code) => {
    const map = {
      INVALID_CSV: t("errInvalidCsv"),
      NO_COLUMNS: t("errNoColumns"),
      NO_EXPENSES: t("errNoExpenses"),
      PDF_UNREADABLE: t("errPdfUnreadable"),
    };
    return map[code] || t("errGeneric");
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter(Boolean);
    if (!incoming.length) return;
    setPending((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      const merged = [...prev];
      for (const f of incoming) {
        const k = `${f.name}|${f.size}|${f.lastModified}`;
        if (!seen.has(k)) { seen.add(k); merged.push(f); }
      }
      return merged;
    });
  };
  const removePending = (idx) => setPending((prev) => prev.filter((_, i) => i !== idx));

  const dkeyOf = (tx, hasBalance, line) => {
    const raw = tx.raw || `${tx.date}|${String(tx.description || "").toLowerCase()}|${Number(tx.amount).toFixed(2)}`;
    return hasBalance ? raw : `${raw}#${line}`;
  };

  const extractOne = async (file) => {
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    const fd = new FormData();
    fd.append("file", file);
    const url = isPdf ? `${API}/pdf/extract` : `${API}/csv/extract`;
    const { data } = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const processFiles = async (files, skipCheck = false) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    if (!skipCheck && !checkOverwrite()) return;

    setLoading(true);
    const combined = [];
    const meta = [];
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      setProgress({ done: i, total: list.length, current: f.name });
      try {
        const r = await extractOne(f);
        const hasBal = !!r.has_balance;
        (r.transactions || []).forEach((tx, j) => combined.push({ ...tx, _dkey: dkeyOf(tx, hasBal, j) }));
        meta.push({ file: f.name, method: r.method, layout_name: r.layout_name, count: r.count || (r.transactions || []).length });
      } catch (e) {
        const msg = errorMessage(e.response?.data?.detail);
        meta.push({ file: f.name, error: msg });
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setProgress(null);

    if (!combined.length) { setLoading(false); return; }

    // Balance-aware dedup. A row's key is its full raw signature, which INCLUDES
    // the running balance when the file has one — so real same-day/same-amount
    // repeats (e.g. multiple Cooltra rides) differ by their progressive balance
    // and are KEPT. Only rows identical in EVERYTHING incl. the balance collapse
    // (same account processed twice). For files WITHOUT a balance column, the
    // row position is added to the key so legitimate repeats are never removed
    // on adjacency alone.
    const kept = [];
    const removed = [];
    const seen = new Set();
    for (const tx of combined) {
      if (seen.has(tx._dkey)) { removed.push(tx); }
      else { seen.add(tx._dkey); kept.push(tx); }
    }

    const strip = (x) => ({ date: x.date, description: x.description, amount: x.amount, raw: x.raw || "", dkey: x._dkey });
    sessionStorage.setItem("frumy_extracted_tx", JSON.stringify(kept.map(strip)));
    sessionStorage.setItem("frumy_extract_removed", JSON.stringify(removed.map(strip)));
    sessionStorage.setItem("frumy_extract_meta", JSON.stringify({ files: meta, dedup_removed: removed.length }));
    sessionStorage.setItem("frumy_account", isPro ? accountName.trim() : "");
    setLoading(false);
    if (meta.some((m) => m.error)) toast.warning(t("someFilesFailed"));
    nav("/anteprima");
  };

  const useDemo = async () => {
    if (!checkOverwrite()) return;
    setLoading(true);
    setProgress({ done: 0, total: 1, current: "frumy_demo.csv" });
    try {
      const res = await axios.get(`${API}/demo-csv`, { responseType: "blob" });
      const file = new File([res.data], "frumy_demo.csv", { type: "text/csv" });
      await processFiles([file], true);
    } catch { toast.error("Errore demo"); setLoading(false); setProgress(null); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button data-testid="upload-back-btn" onClick={() => nav("/dashboard")}
            className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] mb-4">
            <ArrowLeft size={16} aria-hidden />{t("backToDashboard")}
          </button>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--heading)]">{t("upload")}</h1>

          <button data-testid="privacy-message" type="button" onClick={() => nav("/privacy")}
            className="w-full text-left flex items-start gap-3 mt-6 p-4 rounded-lg bg-emerald-50 border border-emerald-200 hover:border-emerald-300 transition-colors cursor-pointer">
            <ShieldCheck weight="fill" size={22} className="text-[var(--frumy-green)] shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm leading-relaxed text-emerald-900">{t("privacy")}</p>
          </button>

          {isPro && (
            <div data-testid="account-name-field" className="mt-6">
              <label className="flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">
                <Bank size={15} aria-hidden />{t("accountName")}
              </label>
              <input data-testid="account-name-input" value={accountName} onChange={(e) => setAccountName(e.target.value)}
                placeholder={t("accountNamePlaceholder")}
                className="mt-1.5 w-full rounded-lg border border-[var(--frumy-border)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
              <p className="text-xs text-[var(--text-muted)] mt-1.5">{t("accountNameHint")}</p>
            </div>
          )}

          <div
            data-testid="dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (!loading) addFiles(e.dataTransfer.files); }}
            onClick={() => !loading && inputRef.current?.click()}
            className={`mt-6 cursor-pointer rounded-xl border-2 border-dashed p-10 sm:p-14 text-center transition-colors ${drag ? "border-[var(--frumy-green)] bg-emerald-50" : "border-[var(--frumy-border)] hover:border-[var(--frumy-green)]"}`}>
            <input ref={inputRef} data-testid="file-input" type="file" multiple accept=".csv,.pdf,text/csv,application/pdf" hidden
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <div className="grid place-items-center gap-4">
              {loading ? (
                <div data-testid="upload-loader" className="w-full max-w-sm grid place-items-center gap-4">
                  <span className="relative grid place-items-center h-16 w-16">
                    <span className="absolute inset-0 rounded-full brand-gradient opacity-20 animate-ping" />
                    <CircleNotch weight="bold" size={44} className="text-[var(--frumy-green)] animate-spin" aria-hidden />
                  </span>
                  <p className="font-bold text-[var(--heading)]">{t("processingFiles")}</p>
                  {progress && (
                    <p className="text-sm text-[var(--text-muted)]">
                      {t("processingFile", { done: Math.min(progress.done + 1, progress.total), total: progress.total, name: progress.current })}
                    </p>
                  )}
                  <div className="w-full h-1.5 rounded-full bg-[var(--frumy-muted)] overflow-hidden">
                    <div className="h-full w-1/3 rounded-full brand-gradient upload-bar" />
                  </div>
                  <p className="text-xs text-[var(--text-muted)] max-w-xs">{t("newLayoutNote")}</p>
                </div>
              ) : (
                <>
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-emerald-50 text-[var(--frumy-green)]">
                    <UploadSimple weight="bold" size={28} aria-hidden />
                  </span>
                  <p className="font-bold text-[var(--heading)] max-w-xs">{t("dropHere")}</p>
                  <p className="text-sm text-[var(--text-muted)] flex items-center gap-1.5"><FileCsv size={16} aria-hidden />{t("csvOrPdfHint")}</p>
                  <p className="text-xs text-[var(--text-muted)] max-w-sm">{t("multiFileHint")}</p>
                </>
              )}
            </div>
          </div>

          {/* Staged files list */}
          {!loading && pending.length > 0 && (
            <div data-testid="pending-files" ref={pendingRef} className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("filesReady", { n: pending.length })}</span>
                <button data-testid="add-file-btn" onClick={() => inputRef.current?.click()}
                  className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
                  <Plus size={15} aria-hidden />{t("addFileBtn")}
                </button>
              </div>
              <div className="space-y-2">
                {pending.map((f, i) => {
                  const isPdf = /\.pdf$/i.test(f.name);
                  return (
                    <div key={i} data-testid="pending-file" className="frumy-card flex items-center justify-between gap-3 px-4 py-2.5">
                      <span className="flex items-center gap-2 text-sm font-semibold text-[var(--heading)] truncate">
                        {isPdf ? <FilePdf weight="fill" size={18} className="text-[var(--frumy-green)] shrink-0" aria-hidden /> : <FileCsv weight="fill" size={18} className="text-[var(--frumy-green)] shrink-0" aria-hidden />}
                        <span className="truncate">{f.name}</span>
                      </span>
                      <button data-testid="remove-pending" onClick={() => removePending(i)} title={t("removeFile")}
                        className="grid place-items-center h-8 w-8 rounded-md text-[var(--text-muted)] hover:bg-red-50 hover:text-red-600 transition-colors shrink-0">
                        <X size={16} aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button data-testid="process-files-btn" onClick={() => processFiles(pending)}
                className="pill-btn cta-metal w-full mt-4 inline-flex items-center justify-center gap-2 text-white font-bold py-3.5">
                <Sparkle weight="fill" size={18} aria-hidden />{t("processFilesBtn", { n: pending.length })}
              </button>
            </div>
          )}

          {!loading && pending.length === 0 && (
            <div className="flex justify-center mt-6">
              <button data-testid="demo-btn" onClick={useDemo}
                className="pill-btn px-5 py-2.5 text-sm font-bold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] flex items-center gap-2">
                <Sparkle size={16} aria-hidden />{t("tryDemo")}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
